import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import MultiSelectDropdown from "../components/common/MultiSelectDropdown";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications"; // UPDATED IMPORTS
import { useDebounce } from "../hook/useDebounce";

const LocationsSubListPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  /* ===== Main Locations Filter State ===== */
  const [mainLocations, setMainLocations] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedMainLocationIds, setSelectedMainLocationIds] = useState(() => {
    if (state?.filterBusinessId) {
      return [String(state.filterBusinessId)];
    }
    return [];
  });

  // Sync if router state changes
  useEffect(() => {
    if (state?.filterBusinessId) {
      setSelectedMainLocationIds([String(state.filterBusinessId)]);
    }
  }, [state?.filterBusinessId]);

  // Fetch Main Location options for dropdown
  useEffect(() => {
    let isMounted = true;
    const fetchMainLocations = async () => {
      setLoadingOptions(true);
      try {
        const res = await api.get("/options/main-locations");
        if (isMounted) {
          setMainLocations(res.data || []);
        }
      } catch (err) {
        console.error("Failed to load main locations for filter", err);
      } finally {
        if (isMounted) setLoadingOptions(false);
      }
    };
    fetchMainLocations();
    return () => {
      isMounted = false;
    };
  }, []);

  // Format dropdown options
  const mainLocationOptions = useMemo(() => {
    return mainLocations.map((loc) => {
      const locId = String(loc.id ?? loc.location_id);
      const name = loc.name || loc.location_name || `Location #${locId}`;
      const addressParts = [loc.city, loc.street_address].filter(Boolean);
      return {
        value: locId,
        label: name,
        sublabel: addressParts.length > 0 ? addressParts.join(" • ") : undefined,
      };
    });
  }, [mainLocations]);

  /* ===== Sorting State ===== */
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  
  const debouncedSearch = useDebounce(search, 500);

  // Fetch sub-locations data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/sub-locations", {
        params: {
          search: debouncedSearch,
          page,
          limit,
          main_location_ids: selectedMainLocationIds.length > 0 ? selectedMainLocationIds.join(",") : undefined,
          sort_by: sortConfig.key,
          order: sortConfig.direction,
        },
      });

      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch {
      showToast("error", "Failed to load sub locations");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, selectedMainLocationIds, sortConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers for active filter chips
  const handleRemoveMainLocation = (idToRemove) => {
    setSelectedMainLocationIds((prev) => prev.filter((id) => String(id) !== String(idToRemove)));
    setPage(1);
    if (state?.filterBusinessId && String(state.filterBusinessId) === String(idToRemove)) {
      navigate("/locations-sub", { replace: true, state: {} });
    }
  };

  const handleClearAllMainLocations = () => {
    setSelectedMainLocationIds([]);
    setPage(1);
    if (state?.filterBusinessId) {
      navigate("/locations-sub", { replace: true, state: {} });
    }
  };

  return (
    <>
      {/* Active Main Location Filter Chips */}
      {selectedMainLocationIds.length > 0 && (
        <div className="mb-12 px-10 d-flex flex-wrap align-items-center gap-2">
          <span className="text-secondary-light text-xs fw-medium me-1">Filtered by Main Location:</span>
          {selectedMainLocationIds.map((id) => {
            const match = mainLocations.find(
              (loc) => String(loc.id) === String(id) || String(loc.location_id) === String(id)
            );
            const name = match ? (match.name || match.location_name) : (state?.filterBusinessName && String(state.filterBusinessId) === String(id) ? state.filterBusinessName : `Location #${id}`);
            return (
              <span
                key={id}
                className="badge bg-primary-50 text-primary-700 border border-primary-200 px-10 py-6 radius-8 pointer d-inline-flex align-items-center gap-2 text-xs"
                onClick={() => handleRemoveMainLocation(id)}
                title="Click to remove filter"
              >
                <span>{name}</span>
                <i className="ri-close-line text-sm"></i>
              </span>
            );
          })}
          <button
            type="button"
            className="btn btn-link p-0 text-secondary-light hover-text-danger text-xs text-decoration-none ms-1"
            onClick={handleClearAllMainLocations}
          >
            Clear all filters
          </button>
        </div>
      )}

      <DataTable
        title="Sub Locations"
        subtitle="Manage units, stores, and sub-facilities within main locations."
        data={data}
        loading={loading}
        searchTerm={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => {
          setLimit(v);
          setPage(1);
        }}
        
        filterComponent={
          <MultiSelectDropdown
            label="Main Locations"
            placeholder="All Main Locations"
            icon="solar:city-linear"
            options={mainLocationOptions}
            selectedValues={selectedMainLocationIds}
            onChange={(vals) => {
              setSelectedMainLocationIds(vals);
              setPage(1);
            }}
            loading={loadingOptions}
          />
        }
        
        showCheckbox={false}

        sortConfig={sortConfig}
        onSortChange={(config) => {
          setSortConfig(config);
          setPage(1);
        }}

        headerActions={[
          {
            label: "Add Unit",
            icon: "ri-add-line",
            className: "btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2 text-xs",
            onClick: () => navigate("/locations-sub/new"),
          },
        ]}

        columns={[
          { 
            key: "name", 
            label: "Unit Name", 
            className: "text-xs fw-semibold text-primary-light",
            sortable: true,
            render: (row) => (
              <div>
                <div className="d-flex align-items-center gap-2">
                  {(row.unit_code || row.store_code) && (
                    <span 
                      className="badge bg-neutral-200 text-neutral-800 px-6 py-2 radius-4 text-xxs font-monospace"
                      title={`Unit Code: ${row.unit_code || row.store_code}`}
                    >
                      {row.unit_code || row.store_code}
                    </span>
                  )}
                  <span className="fw-semibold text-dark text-xs">{row.name}</span>
                </div>
                {row.description && (
                  <span className="text-secondary-light text-xxs d-block text-truncate mt-1" style={{ maxWidth: "220px" }}>
                    {row.description}
                  </span>
                )}
              </div>
            )
          },
          { 
            key: "type", 
            label: "Type / Category", 
            sortable: true,
            render: (row) => (
              <span className="bg-info-focus text-info-main px-8 py-2 radius-4 fw-medium text-xs">
                {row.primary_category_id || row.type || "N/A"}
              </span>
            ) 
          },
          { 
            key: "level", 
            label: "Floor / Level", 
            sortable: true,
            render: (row) => (
              <span className="badge bg-neutral-100 text-secondary-light px-8 py-4 radius-4 text-xs font-monospace">
                {row.level || "—"}
              </span>
            )
          },
          { 
            key: "parent_main_name", 
            label: "Parent Business", 
            className: "text-xs text-secondary-light fw-medium",
            sortable: true 
          },
          {
            key: "status",
            label: "Status",
            sortable: true,
            render: (row) => {
              const status = row.status || "ACTIVE";
              const isInactive = status === "INACTIVE" || status === "VACANT";
              return (
                <span className={`badge px-8 py-2 radius-4 text-xxs fw-medium ${
                  isInactive ? "bg-warning-focus text-warning-main" : "bg-success-focus text-success-main"
                }`}>
                  {status}
                </span>
              );
            }
          },
          {
            key: "map_status",
            label: "Geo Data",
            render: (row) => {
              const hasPin = row.lat && row.lng;
              const hasOutline = Boolean(row.geometrics_outline);
              return (
                <div className="d-flex align-items-center gap-1">
                  <span
                    className={`badge radius-4 px-6 py-2 text-xxs d-inline-flex align-items-center gap-1 ${
                      hasPin ? "bg-success-subtle text-success-700" : "bg-neutral-100 text-neutral-400"
                    }`}
                    title={hasPin ? `Pin: ${Number(row.lat).toFixed(4)}, ${Number(row.lng).toFixed(4)}` : "No pin set"}
                  >
                    <i className="ri-map-pin-line"></i> {hasPin ? "Pin" : "No Pin"}
                  </span>
                  <span
                    className={`badge radius-4 px-6 py-2 text-xxs d-inline-flex align-items-center gap-1 ${
                      hasOutline ? "bg-primary-subtle text-primary-700" : "bg-neutral-100 text-neutral-400"
                    }`}
                    title={hasOutline ? "Building outline set" : "No outline"}
                  >
                    <i className="ri-shape-line"></i> {hasOutline ? "Outline" : "No Outline"}
                  </span>
                </div>
              );
            }
          }
        ]}

        actions={[
          {
            icon: "lucide:edit",
            className: "w-28-px h-28-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
            onClick: (row) => navigate(`/locations-sub/${row.id}/edit`),
          },
          {
            icon: "mingcute:delete-2-line",
            className: "w-28-px h-28-px bg-danger-focus text-danger-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
            onClick: (row) => {
              confirmDelete(row.name, async () => {
                try {
                  await api.delete(`/sub-locations/${row.id}`);
                  showToast("success", "Sub location deleted");
                  fetchData();
                } catch {
                  showToast("error", "Delete operation failed");
                }
              });
            },
          },
        ]}
      />
    </>
  );
};

export default LocationsSubListPage;